import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { authAPI } from '../api/client';
import BrandLogo from '../components/BrandLogo';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [resetUrl, setResetUrl] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setResetUrl('');
    setIsSubmitting(true);

    try {
      const result = await authAPI.requestPasswordReset(email);
      setMessage(result.message);
      if (result.reset_url) {
        setResetUrl(result.reset_url);
      }
    } catch (err: unknown) {
      let detail = 'Something went wrong requesting a password reset. Please try again.';
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { email?: string[]; detail?: string } | undefined;
        detail = data?.email?.[0] || data?.detail || detail;
      }
      setError(detail);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="standalone-screen flex justify-center p-4">
      <div className="w-full max-w-md m-auto">
        <div className="text-center mb-8">
          <BrandLogo className="h-28 w-28 mx-auto" />
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h2 className="text-xl font-semibold text-navy mb-2 text-center">
            Reset your password
          </h2>
          <p className="text-gray-600 text-base mb-6 text-center">
            Enter your account email and we'll send you a link to choose a new password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-navy focus:border-transparent outline-none transition text-base"
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            {message && (
              <div className="bg-green-50 text-green-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{message}</span>
              </div>
            )}

            {resetUrl && (
              <div className="bg-gold/10 text-navy px-4 py-3 rounded-lg text-sm">
                <p className="font-medium mb-2">Sandbox reset link:</p>
                <a href={resetUrl} className="underline break-all">
                  {resetUrl}
                </a>
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-navy text-white py-3 rounded-lg font-semibold text-base hover:bg-navy-dark transition disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Sending link...
                </>
              ) : (
                'Send reset link'
              )}
            </button>
          </form>

          <Link
            to="/login"
            className="block text-center text-sm text-navy hover:text-gold font-medium mt-6"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
